import React from 'react';
import { Phone, AlertTriangle, XCircle, Clock, FileText, Bell } from 'lucide-react';
export function TrafficViolationConvictionsPage() {
  return <div className="min-h-screen bg-gray-50" data-id="element-3727">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-[#B1050B] to-[#8A0408] text-white py-16" data-id="element-3728">
        <div className="container mx-auto px-4" data-id="element-3729">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-center" data-id="element-3730">
            Traffic Violation Convictions
          </h1>
          <p className="text-xl text-gray-100 text-center" data-id="element-3731">
            While Driving a Commercial Motor Vehicle
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12" data-id="element-3732">
        <div className="max-w-4xl mx-auto" data-id="element-3733">
          {/* Overview */}
          <div className="bg-red-50 border-2 border-red-500 rounded-lg shadow-md p-8 mb-8" data-id="element-3734">
            <div className="flex items-start" data-id="element-3735">
              <AlertTriangle className="text-red-600 mr-4 flex-shrink-0 mt-1" size={32} data-id="element-3736" />
              <div data-id="element-3737">
                <h2 className="text-2xl font-bold text-red-900 mb-4" data-id="element-3738">
                  Important Notice
                </h2>
                <p className="text-gray-800 mb-3" data-id="element-3739">
                  If you are convicted of certain traffic offenses while
                  operating a commercial motor vehicle (CMV), you will be
                  subject to <strong data-id="element-3740">more severe penalties</strong>, which may
                  result in the{' '}
                  <strong data-id="element-3741">suspension or revocation of your CDL</strong>.
                </p>
                <p className="text-gray-800 mb-3" data-id="element-3742">
                  Your operator/chauffeur license may also be affected,
                  depending on the severity of the traffic offense.
                </p>
                <p className="text-gray-800" data-id="element-3743">
                  <strong data-id="element-3744">
                    Any traffic conviction in a non-commercial motor vehicle
                    that results in a suspension or revocation of your
                    operator/chauffeur license, will also apply to your CDL.
                  </strong>
                </p>
              </div>
            </div>
          </div>

          {/* Major Traffic Offenses */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-3745">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center" data-id="element-3746">
              <XCircle className="mr-3 text-[#B1050B]" size={28} data-id="element-3747" />
              Major Traffic Offenses
            </h2>

            <div className="bg-red-50 rounded-lg p-6 mb-6" data-id="element-3748">
              <h3 className="font-bold text-gray-900 mb-4" data-id="element-3749">
                Major Offenses Include:
              </h3>
              <ul className="space-y-2 text-gray-700" data-id="element-3750">
                <li className="flex items-start" data-id="element-3751">
                  <span className="text-red-600 mr-2 flex-shrink-0" data-id="element-3752">•</span>
                  <span data-id="element-3753">Under the influence of alcohol</span>
                </li>
                <li className="flex items-start" data-id="element-3754">
                  <span className="text-red-600 mr-2 flex-shrink-0" data-id="element-3755">•</span>
                  <span data-id="element-3756">
                    Having an alcohol concentration of{' '}
                    <strong data-id="element-3757">0.04 or greater</strong> while operating a CMV
                  </span>
                </li>
                <li className="flex items-start" data-id="element-3758">
                  <span className="text-red-600 mr-2 flex-shrink-0" data-id="element-3759">•</span>
                  <span data-id="element-3760">
                    Operating under the influence of a controlled substance
                  </span>
                </li>
                <li className="flex items-start" data-id="element-3761">
                  <span className="text-red-600 mr-2 flex-shrink-0" data-id="element-3762">•</span>
                  <span data-id="element-3763">Refusing to take an alcohol test</span>
                </li>
                <li className="flex items-start" data-id="element-3764">
                  <span className="text-red-600 mr-2 flex-shrink-0" data-id="element-3765">•</span>
                  <span data-id="element-3766">Leaving the scene of an accident</span>
                </li>
                <li className="flex items-start" data-id="element-3767">
                  <span className="text-red-600 mr-2 flex-shrink-0" data-id="element-3768">•</span>
                  <span data-id="element-3769">Using the vehicle to commit a felony</span>
                </li>
                <li className="flex items-start" data-id="element-3770">
                  <span className="text-red-600 mr-2 flex-shrink-0" data-id="element-3771">•</span>
                  <span data-id="element-3772">
                    Causing a fatality through the negligent operation of a CMV
                  </span>
                </li>
                <li className="flex items-start" data-id="element-3773">
                  <span className="text-red-600 mr-2 flex-shrink-0" data-id="element-3774">•</span>
                  <span data-id="element-3775">
                    Using the vehicle in the commission of a felony involving
                    manufacturing, distributing or dispensing a controlled
                    substance
                  </span>
                </li>
              </ul>
            </div>

            <div className="space-y-4" data-id="element-3776">
              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6" data-id="element-3777">
                <p className="font-bold text-gray-900 mb-3" data-id="element-3778">
                  If charged and convicted for any major traffic offense while
                  driving any type of motor vehicle, your CDL will be:
                </p>
                <ul className="space-y-2 text-gray-700" data-id="element-3779">
                  <li className="flex items-start" data-id="element-3780">
                    <span className="mr-2" data-id="element-3781">•</span>
                    <span data-id="element-3782">
                      <strong data-id="element-3783">Suspended for 1 year</strong> for a first
                      violation; <strong data-id="element-3784">3 years</strong> if transporting
                      hazardous materials
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-3785">
                    <span className="mr-2" data-id="element-3786">•</span>
                    <span data-id="element-3787">
                      <strong data-id="element-3788">Revoked for a minimum of 10 years</strong> for any
                      two convictions occurring in separate incidents, in any
                      time period
                    </span>
                  </li>
                </ul>
              </div>

              <div className="bg-orange-50 border-l-4 border-orange-500 p-6" data-id="element-3789">
                <p className="text-gray-700 mb-2" data-id="element-3790">
                  If charged with and convicted of{' '}
                  <strong data-id="element-3791">
                    reckless driving, negligent homicide, or fleeing/eluding a
                    law enforcement officer (misdemeanor)
                  </strong>
                  , your CDL will be <strong data-id="element-3792">suspended for one year</strong>.
                </p>
              </div>

              <div className="bg-red-50 border-l-4 border-red-500 p-6" data-id="element-3793">
                <p className="text-gray-700" data-id="element-3794">
                  If charged with and convicted of committing a{' '}
                  <strong data-id="element-3795">
                    felony involving the manufacture, distribution or dispensing
                    of a controlled substance
                  </strong>
                  , your CDL will be <strong data-id="element-3796">revoked for life</strong>.
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-4" data-id="element-3797">
                <p className="text-sm text-gray-700" data-id="element-3798">
                  Your operator/chauffeur license may also be affected,
                  depending on the severity of the traffic conviction and points
                  will be added to your driving record.
                </p>
              </div>
            </div>
          </div>

          {/* Serious Traffic Violations */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-3799">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center" data-id="element-3800">
              <AlertTriangle className="mr-3 text-orange-600" size={28} data-id="element-3801" />
              Serious Traffic Violations
            </h2>

            <div className="bg-orange-50 rounded-lg p-6 mb-6" data-id="element-3802">
              <h3 className="font-bold text-gray-900 mb-4" data-id="element-3803">
                Serious Violations Include:
              </h3>
              <div className="grid md:grid-cols-2 gap-3" data-id="element-3804">
                <ul className="space-y-2 text-gray-700" data-id="element-3805">
                  <li className="flex items-start" data-id="element-3806">
                    <span className="text-orange-600 mr-2 flex-shrink-0" data-id="element-3807">
                      •
                    </span>
                    <span data-id="element-3808">Speeding 15 mph or more over the posted limit</span>
                  </li>
                  <li className="flex items-start" data-id="element-3809">
                    <span className="text-orange-600 mr-2 flex-shrink-0" data-id="element-3810">
                      •
                    </span>
                    <span data-id="element-3811">Following too closely</span>
                  </li>
                  <li className="flex items-start" data-id="element-3812">
                    <span className="text-orange-600 mr-2 flex-shrink-0" data-id="element-3813">
                      •
                    </span>
                    <span data-id="element-3814">Changing/using lanes erratically/improperly</span>
                  </li>
                  <li className="flex items-start" data-id="element-3815">
                    <span className="text-orange-600 mr-2 flex-shrink-0" data-id="element-3816">
                      •
                    </span>
                    <span data-id="element-3817">Operating a CMV without a CDL in possession</span>
                  </li>
                </ul>
                <ul className="space-y-2 text-gray-700" data-id="element-3818">
                  <li className="flex items-start" data-id="element-3819">
                    <span className="text-orange-600 mr-2 flex-shrink-0" data-id="element-3820">
                      •
                    </span>
                    <span data-id="element-3821">
                      A traffic violation connected with a fatal accident
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-3822">
                    <span className="text-orange-600 mr-2 flex-shrink-0" data-id="element-3823">
                      •
                    </span>
                    <span data-id="element-3824">Careless driving</span>
                  </li>
                  <li className="flex items-start" data-id="element-3825">
                    <span className="text-orange-600 mr-2 flex-shrink-0" data-id="element-3826">
                      •
                    </span>
                    <span data-id="element-3827">Operating a CMV without a CDL</span>
                  </li>
                  <li className="flex items-start" data-id="element-3828">
                    <span className="text-orange-600 mr-2 flex-shrink-0" data-id="element-3829">
                      •
                    </span>
                    <span data-id="element-3830">
                      Driving a CMV without proper class of CDL and/or
                      endorsements
                    </span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="space-y-4" data-id="element-3831">
              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6" data-id="element-3832">
                <p className="font-bold text-gray-900 mb-3" data-id="element-3833">
                  If charged and convicted for any serious traffic violation
                  while driving a CMV, your CDL will be:
                </p>
                <ul className="space-y-2 text-gray-700" data-id="element-3834">
                  <li className="flex items-start" data-id="element-3835">
                    <span className="mr-2" data-id="element-3836">•</span>
                    <span data-id="element-3837">
                      <strong data-id="element-3838">Suspended for 60 days</strong> for two violations
                      in separate incidents in three years
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-3839">
                    <span className="mr-2" data-id="element-3840">•</span>
                    <span data-id="element-3841">
                      <strong data-id="element-3842">Suspended for 120 days</strong> for three or more
                      violations in separate incidents in three years
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-3843">
                    <span className="mr-2" data-id="element-3844">•</span>
                    <span data-id="element-3845">Points will be added to your driving record</span>
                  </li>
                </ul>
              </div>

              <div className="bg-blue-50 rounded-lg p-4" data-id="element-3846">
                <p className="text-sm text-gray-700" data-id="element-3847">
                  If convicted for any of the above in a{' '}
                  <strong data-id="element-3848">non-CMV</strong>, your CDL will be suspended if the
                  conviction results in a suspension or revocation of your
                  driver's license.
                </p>
              </div>
            </div>
          </div>

          {/* Railroad Crossing Violations */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-3849">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-3850">
              Railroad Crossing Violations
            </h2>

            <div className="space-y-6" data-id="element-3851">
              <div className="bg-blue-50 rounded-lg p-6" data-id="element-3852">
                <h3 className="font-bold text-blue-900 mb-3" data-id="element-3853">
                  For drivers who are NOT required to always stop:
                </h3>
                <ul className="space-y-2 text-gray-700" data-id="element-3854">
                  <li className="flex items-start" data-id="element-3855">
                    <span className="text-blue-600 mr-2 flex-shrink-0" data-id="element-3856">•</span>
                    <span data-id="element-3857">
                      Failure to slow down and check the tracks are clear of an
                      approaching train
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-3858">
                    <span className="text-blue-600 mr-2 flex-shrink-0" data-id="element-3859">•</span>
                    <span data-id="element-3860">
                      Failure to stop before reaching the crossing when the
                      tracks are not clear
                    </span>
                  </li>
                </ul>
              </div>

              <div className="bg-green-50 rounded-lg p-6" data-id="element-3861">
                <h3 className="font-bold text-green-900 mb-3" data-id="element-3862">
                  For drivers who ARE always required to stop:
                </h3>
                <ul className="space-y-2 text-gray-700" data-id="element-3863">
                  <li className="flex items-start" data-id="element-3864">
                    <span className="text-green-600 mr-2 flex-shrink-0" data-id="element-3865">•</span>
                    <span data-id="element-3866">
                      Failing to stop before driving onto the crossing
                    </span>
                  </li>
                </ul>
              </div>

              <div className="bg-purple-50 rounded-lg p-6" data-id="element-3867">
                <h3 className="font-bold text-purple-900 mb-3" data-id="element-3868">
                  For ALL drivers:
                </h3>
                <ul className="space-y-2 text-gray-700" data-id="element-3869">
                  <li className="flex items-start" data-id="element-3870">
                    <span className="text-purple-600 mr-2 flex-shrink-0" data-id="element-3871">
                      •
                    </span>
                    <span data-id="element-3872">
                      Failing to have sufficient space to drive completely
                      through the crossing without stopping
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-3873">
                    <span className="text-purple-600 mr-2 flex-shrink-0" data-id="element-3874">
                      •
                    </span>
                    <span data-id="element-3875">
                      Failing to obey a traffic control device or the directions
                      of an enforcement official at the crossing
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-3876">
                    <span className="text-purple-600 mr-2 flex-shrink-0" data-id="element-3877">
                      •
                    </span>
                    <span data-id="element-3878">
                      Failing to negotiate a crossing because of insufficient
                      undercarriage clearance
                    </span>
                  </li>
                </ul>
              </div>

              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6" data-id="element-3879">
                <p className="font-bold text-gray-900 mb-3" data-id="element-3880">
                  If charged and convicted for any railroad crossing violation,
                  your CDL will be:
                </p>
                <ul className="space-y-2 text-gray-700" data-id="element-3881">
                  <li className="flex items-start" data-id="element-3882">
                    <span className="mr-2" data-id="element-3883">•</span>
                    <span data-id="element-3884">
                      <strong data-id="element-3885">Suspended for 60 days</strong> for the first
                      violation
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-3886">
                    <span className="mr-2" data-id="element-3887">•</span>
                    <span data-id="element-3888">
                      <strong data-id="element-3889">Suspended for 120 days</strong> for two violations
                      in separate incidents in three years
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-3890">
                    <span className="mr-2" data-id="element-3891">•</span>
                    <span data-id="element-3892">
                      <strong data-id="element-3893">Suspended for one year</strong> for three or more
                      violations in separate incidents in three years
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-3894">
                    <span className="mr-2" data-id="element-3895">•</span>
                    <span data-id="element-3896">Points will be added to your driving record</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Serious Alcohol Violations */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-3897">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center" data-id="element-3898">
              <Clock className="mr-3 text-[#B1050B]" size={28} data-id="element-3899" />
              Serious Alcohol Violations While Operating a CMV
            </h2>

            <div className="space-y-6" data-id="element-3900">
              <div className="bg-red-50 rounded-lg p-6" data-id="element-3901">
                <h3 className="font-bold text-red-900 mb-4" data-id="element-3902">
                  Out-of-Service (24 hours)
                </h3>
                <p className="text-gray-700 mb-3" data-id="element-3903">
                  You will be placed out-of-service for 24 hours if you:
                </p>
                <ul className="space-y-2 text-gray-700" data-id="element-3904">
                  <li className="flex items-start" data-id="element-3905">
                    <span className="text-red-600 mr-2 flex-shrink-0" data-id="element-3906">•</span>
                    <span data-id="element-3907">Refuse to take a preliminary breath test (PBT)</span>
                  </li>
                  <li className="flex items-start" data-id="element-3908">
                    <span className="text-red-600 mr-2 flex-shrink-0" data-id="element-3909">•</span>
                    <span data-id="element-3910">
                      Consume alcohol within four hours prior to operating a CMV
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-3911">
                    <span className="text-red-600 mr-2 flex-shrink-0" data-id="element-3912">•</span>
                    <span data-id="element-3913">Consume alcohol while operating a CMV</span>
                  </li>
                  <li className="flex items-start" data-id="element-3914">
                    <span className="text-red-600 mr-2 flex-shrink-0" data-id="element-3915">•</span>
                    <span data-id="element-3916">
                      Have a bodily alcohol content (BAC) of{' '}
                      <strong data-id="element-3917">.015 grams or more</strong> per 100 milliliters of
                      blood, per 210 liters of breath, or per 67 milliliters of
                      urine
                    </span>
                  </li>
                </ul>
              </div>

              <div className="bg-orange-50 rounded-lg p-6" data-id="element-3918">
                <h3 className="font-bold text-orange-900 mb-4" data-id="element-3919">
                  Other Alcohol Violations
                </h3>
                <p className="text-gray-700 mb-3" data-id="element-3920">
                  If you have consumed too much alcohol, you can be charged
                  with:
                </p>
                <ul className="space-y-2 text-gray-700" data-id="element-3921">
                  <li className="flex items-start" data-id="element-3922">
                    <span className="text-orange-600 mr-2 flex-shrink-0" data-id="element-3923">
                      •
                    </span>
                    <span data-id="element-3924">Operating While Intoxicated</span>
                  </li>
                  <li className="flex items-start" data-id="element-3925">
                    <span className="text-orange-600 mr-2 flex-shrink-0" data-id="element-3926">
                      •
                    </span>
                    <span data-id="element-3927">Operating While Impaired by Liquor</span>
                  </li>
                  <li className="flex items-start" data-id="element-3928">
                    <span className="text-orange-600 mr-2 flex-shrink-0" data-id="element-3929">
                      •
                    </span>
                    <span data-id="element-3930">
                      Operating with a BAC of{' '}
                      <strong data-id="element-3931">.04 &lt; .08 grams</strong> per 100 milliliters of
                      blood, per 210 liters of breath, or per 67 milliliters of
                      urine
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-3932">
                    <span className="text-orange-600 mr-2 flex-shrink-0" data-id="element-3933">
                      •
                    </span>
                    <span data-id="element-3934">
                      OUIL, OWI or UBAL causing death or serious injury
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-3935">
                    <span className="text-orange-600 mr-2 flex-shrink-0" data-id="element-3936">
                      •
                    </span>
                    <span data-id="element-3937">
                      Operating under the influence, or operating while impaired
                      by Controlled Substances
                    </span>
                  </li>
                </ul>
              </div>

              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6" data-id="element-3938">
                <p className="font-bold text-gray-900 mb-3" data-id="element-3939">
                  If you refuse to submit to a chemical test (Implied Consent)*
                  or are convicted of any alcohol offense, your CDL will be:
                </p>
                <ul className="space-y-2 text-gray-700" data-id="element-3940">
                  <li className="flex items-start" data-id="element-3941">
                    <span className="mr-2" data-id="element-3942">•</span>
                    <span data-id="element-3943">
                      <strong data-id="element-3944">Suspended for one year</strong> for a first
                      offense, or <strong data-id="element-3945">three years</strong> if transporting
                      hazardous materials
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-3946">
                    <span className="mr-2" data-id="element-3947">•</span>
                    <span data-id="element-3948">
                      <strong data-id="element-3949">Revoked for a minimum of ten years</strong> for a
                      second or third offense
                    </span>
                  </li>
                </ul>
              </div>

              <div className="bg-blue-50 rounded-lg p-4" data-id="element-3950">
                <p className="text-sm text-gray-700" data-id="element-3951">
                  <strong data-id="element-3952">Note:</strong> *Implied Consent means that any person
                  who holds a CDL is considered to have agreed to such testing.
                  Consent is implied by driving a motor vehicle.
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-4" data-id="element-3953">
                <p className="text-sm text-gray-700" data-id="element-3954">
                  Your operator/chauffeur license may also be suspended or
                  revoked and points may be added to your driving record.
                </p>
              </div>
            </div>
          </div>

          {/* Notification Requirements */}
          <div className="bg-gradient-to-r from-[#B1050B] to-[#8A0408] text-white rounded-lg shadow-xl p-8 mb-8" data-id="element-3955">
            <h2 className="text-3xl font-bold mb-6 flex items-center" data-id="element-3956">
              <Bell className="mr-3" size={32} data-id="element-3957" />
              CMV Operator's Responsibilities: Notification Requirements
            </h2>

            <div className="space-y-6" data-id="element-3958">
              <div className="bg-white/10 rounded-lg p-6" data-id="element-3959">
                <h3 className="font-bold text-xl mb-4" data-id="element-3960">
                  You must notify your employer in writing of:
                </h3>

                <div className="space-y-4" data-id="element-3961">
                  <div className="bg-white/10 rounded-lg p-4" data-id="element-3962">
                    <p className="font-semibold mb-2" data-id="element-3963">Traffic Violations</p>
                    <p className="text-gray-100 text-sm mb-3" data-id="element-3964">
                      (Other than a parking ticket) committed in any motor
                      vehicle <strong data-id="element-3965">within 30 days of conviction</strong>
                    </p>
                    <p className="text-gray-100 text-sm mb-2" data-id="element-3966">
                      The notification must include:
                    </p>
                    <ul className="space-y-1 text-sm text-gray-200 ml-4" data-id="element-3967">
                      <li data-id="element-3968">• Your full name and signature</li>
                      <li data-id="element-3969">• Driver license number</li>
                      <li data-id="element-3970">• Date of conviction</li>
                      <li data-id="element-3971">• Specific violation(s) committed</li>
                      <li data-id="element-3972">• Whether the violation was in a CMV</li>
                    </ul>
                  </div>

                  <div className="bg-white/10 rounded-lg p-4" data-id="element-3973">
                    <p className="font-semibold mb-2" data-id="element-3974">Out-of-Service Orders</p>
                    <p className="text-gray-100 text-sm" data-id="element-3975">
                      Any out-of-service order{' '}
                      <strong data-id="element-3976">within twenty-four hours</strong>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Penalty Summary */}
          <div className="bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg shadow-xl p-8 mb-8" data-id="element-3977">
            <h2 className="text-3xl font-bold mb-6" data-id="element-3978">Penalty Summary</h2>

            <div className="grid md:grid-cols-2 gap-6" data-id="element-3979">
              <div data-id="element-3980">
                <h3 className="text-xl font-bold mb-3" data-id="element-3981">Major Offenses</h3>
                <ul className="space-y-2 text-sm" data-id="element-3982">
                  <li data-id="element-3983">• 1st violation: 1 year (3 years hazmat)</li>
                  <li data-id="element-3984">• 2 violations: 10 years minimum</li>
                  <li data-id="element-3985">• Drug felony: Life revocation</li>
                </ul>
              </div>

              <div data-id="element-3986">
                <h3 className="text-xl font-bold mb-3" data-id="element-3987">Serious Violations</h3>
                <ul className="space-y-2 text-sm" data-id="element-3988">
                  <li data-id="element-3989">• 2 violations in 3 years: 60 days</li>
                  <li data-id="element-3990">• 3+ violations in 3 years: 120 days</li>
                </ul>
              </div>

              <div data-id="element-3991">
                <h3 className="text-xl font-bold mb-3" data-id="element-3992">Railroad Crossings</h3>
                <ul className="space-y-2 text-sm" data-id="element-3993">
                  <li data-id="element-3994">• 1st violation: 60 days</li>
                  <li data-id="element-3995">• 2 violations in 3 years: 120 days</li>
                  <li data-id="element-3996">• 3+ violations in 3 years: 1 year</li>
                </ul>
              </div>

              <div data-id="element-3997">
                <h3 className="text-xl font-bold mb-3" data-id="element-3998">Alcohol Violations</h3>
                <ul className="space-y-2 text-sm" data-id="element-3999">
                  <li data-id="element-4000">• 1st offense: 1 year (3 years hazmat)</li>
                  <li data-id="element-4001">• 2nd/3rd offense: 10 years minimum</li>
                  <li data-id="element-4002">• Out-of-service: 24 hours</li>
                </ul>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white rounded-lg shadow-xl p-8 text-center" data-id="element-4003">
            <h2 className="text-3xl font-bold mb-4" data-id="element-4004">
              Need Help with CDL Compliance?
            </h2>
            <p className="text-xl mb-6" data-id="element-4005">
              Expert guidance on traffic violations and CDL requirements
            </p>
            <a href="tel:2484788382" className="inline-flex items-center bg-[#B1050B] hover:bg-[#8A0408] text-white px-8 py-4 rounded-lg font-bold text-lg transition-colors shadow-lg" data-id="element-4006">
              <Phone className="mr-3" size={24} data-id="element-4007" />
              Call (248) 478-8382
            </a>
            <p className="mt-4 text-gray-300" data-id="element-4008">
              24175 Industrial Park Dr., Farmington Hills, MI 48335
            </p>
          </div>
        </div>
      </div>
    </div>;
}